﻿import React, { useState, ChangeEvent } from 'react';
import { Button, ButtonGroup, Checkbox, Col, Panel, Row, FormGroup, Modal} from 'react-bootstrap';
import moment from 'moment';
import { isAfter, isSameDay, addDays, setHours, setMinutes } from 'date-fns';
import {
    BookableOrderTypes,
    IBookingInfo,
    IImageEditorViewModel,
    ISelectViewModel,
    NoBookingDecision
} from '../../Types';
import { Translate } from '../../../../../../shared/LanguageService';
import * as Site from '../../../../../../shared/Site';
import * as Features from '../../../../../../shared/Features';
import DateTimeView from '../../../../../localDate/DateTimeView';
import FormSelect from '../../../../../../components/common/FormSelect';
import * as PhotoStatus from '../../../../../../shared/PhotoStatus';
import ArchiveSampleImagesModal from '../../ArchiveSampleImagesModal';
import * as UnarchiveStatus from '../../UnarchivedStatus';
import { TranslatedLabels } from '../../../../../../shared/types';
import { getBrokerAgentPhotoStatus, isBrokerAgent } from '../../../../utils/brokerAgentUtils';
import {
    isPhotoOrderArchived,
    lockPhotographer
} from '../utils';
import ScandphotosPhotographerBookingInfo from './ScandphotosPhotographerBookingInfo';
import SE360PhotographerBookingInfo from './SE360PhotographerBookingInfo';
import AutoResizeTextArea from './../../../imageprocessing/AutoResizeTextArea';
import { CommentType } from './../../../imageprocessing/Entities';

type PhotoOrderInfoProps = {
    bookingInfo: IBookingInfo;
    site: string;
    user: any;
    updateImageEditorFirmChanged: (event: any) => void;
    updateImageEditorChanged: (event: any) => void;
    disableImageEditorFirm: (photoStatus: any) => boolean;
    availableImageEditorFirms: ISelectViewModel[];
    disableImageEditor: (bookingInfo: IBookingInfo) => boolean;
    availableImageEditors: IImageEditorViewModel[];
    canChangeIsDirectDelivery: (photoStatusValue: number) => boolean;
    onIsDirectDeliveryChanged: (event: any) => void;
    numberOfOrderRows: number;
    setOrderToUploader: () => void;
    markAsUnread: () => void;
    unarchivePhoto: () => void;
    onToggleArchiveSampleImagesModal: (someValue: boolean) => void;
    showArchiveSampleImagesModal: boolean;
    unbookPhotographer: () => void;
    orderId: number;
    navigateTo: (url: string) => void;
    lockPhotographerAction: () => void;
    lockPhotographerReason: (bookingInfo: IBookingInfo) => string;
    unlockPhotographer: () => void;
    labels: TranslatedLabels;
    saveComment: (text: string, type: number) => void;
  };

export function PhotoOrderInfo({
    bookingInfo,
    site,
    user,
    updateImageEditorFirmChanged,
    updateImageEditorChanged,
    disableImageEditorFirm,
    availableImageEditorFirms,
    disableImageEditor,
    availableImageEditors,
    canChangeIsDirectDelivery,
    onIsDirectDeliveryChanged,
    numberOfOrderRows,
    setOrderToUploader,
    markAsUnread,
    unarchivePhoto,
    onToggleArchiveSampleImagesModal,
    showArchiveSampleImagesModal,
    unbookPhotographer,
    orderId,
    navigateTo,
    lockPhotographerAction,
    lockPhotographerReason,
    unlockPhotographer,
    labels,
    saveComment
}: PhotoOrderInfoProps) {
    const [isMsgAgentToPhotographerModalVisible, setIsMsgAgentToPhotographerModalVisible] = useState(false);
    const [msgAgentToPhotographer, setMsgAgentToPhotographer] = useState('');

    const userIsBrokerAgent = isBrokerAgent(user.details.role);

    const now = new Date();
    const tomorrow = addDays(now, 1);
    const bookedTime = bookingInfo && bookingInfo.bookedTime && bookingInfo.bookedTime.localDateTime 
        ? new Date(bookingInfo.bookedTime.localDateTime) 
        : null;
    // Set the time to 18:00 to cut off the possibility to add comment to Photographer
    const canWriteMessageToPhotographer = userIsBrokerAgent && bookedTime && (
            isAfter(bookedTime, tomorrow) 
        || (isSameDay(bookedTime, tomorrow) && isAfter(setMinutes(setHours(now, 18), 0), now))
    );

    let photoStatusTranslation = '';
    if (bookingInfo.photoStatus) {
        photoStatusTranslation = bookingInfo.photoStatus.displayName;
    }

    const photoStatus = userIsBrokerAgent
        ? getBrokerAgentPhotoStatus(photoStatusTranslation, labels.customerPhotoStatus)
        : Translate(photoStatusTranslation);

    const photoBookingDecision = bookingInfo.photoBookingDecision || NoBookingDecision;
    const goToBooking = () =>
        navigateTo(`${photoBookingDecision.bookingPath}&redirectTo=/orders/${orderId}`);

        
    return (
        <Panel>
            <Panel.Heading>
                {bookingInfo &&
                bookingInfo.bookableOrderType &&
                bookingInfo.bookableOrderType.value === BookableOrderTypes.Styling ? (
                    <h3 className="panel-title">
                        {Translate('view-order/basic-order-info/panel-headers/styling')}
                    </h3>
                ) : (
                    <h3 className="panel-title">
                        {Translate('view-order/basic-order-info/panel-headers/photo')}
                    </h3>
                )}
            </Panel.Heading>
            <Panel.Body>
                <dl>
                    <Row>
                        <Col sm={6}>
                            {bookingInfo &&
                            bookingInfo.bookableOrderType &&
                            bookingInfo.bookableOrderType.value === BookableOrderTypes.Styling ? (
                                <dt>
                                    {Translate('view-order/basic-order-info/titles/styling-status')}
                                </dt>
                            ) : (
                                <dt>
                                    {Translate('view-order/basic-order-info/titles/photo-status')}
                                </dt>
                            )}

                            <dd>{photoStatus}</dd>
                        </Col>
                        <Col sm={6}>
                            <dt>{Translate('view-order/basic-order-info/titles/booking-type')}</dt>
                            <dd>
                                {bookingInfo && bookingInfo.bookingType
                                    ? Translate(bookingInfo.bookingType.displayName)
                                    : ''}
                            </dd>
                        </Col>
                    </Row>

                    <Row>
                        {bookingInfo &&
                        bookingInfo.bookableOrderType &&
                        bookingInfo.bookableOrderType.value === BookableOrderTypes.Photo &&
                        bookingInfo.photographerName ? (
                            site === Site.getSiteName(Site.Scandphotos) ? (
                                <ScandphotosPhotographerBookingInfo
                                    user={user}
                                    bookedTime={bookingInfo.bookedTime.localDateTime}
                                    photographerName={bookingInfo.photographerName}
                                />
                            ) : (
                                <SE360PhotographerBookingInfo
                                    user={user}
                                    photographerName={bookingInfo.photographerName}
                                    photographerId={bookingInfo.photographerId}
                                    navigateTo={navigateTo}
                                />
                            )
                        ) : null}
                        {bookingInfo.bookedTime && (
                            <Col sm={6}>
                                <dt>
                                    {Translate('view-order/basic-order-info/titles/performed-time')}
                                </dt>
                                <dd>
                                    <DateTimeView dateTime={bookingInfo.completedTime} />
                                </dd>
                            </Col>
                        )}
                    </Row>

                    {bookingInfo &&
                    bookingInfo.bookableOrderType &&
                    bookingInfo.bookableOrderType.value === BookableOrderTypes.Photo ? (
                        <Row>
                            <Col sm={6}>
                                <dt>
                                    {Translate('view-order/basic-order-info/titles/will-attend')}
                                </dt>
                                <dd>
                                    {bookingInfo.willOrderCreatorAttendWhenPhotographing === true
                                        ? Translate(
                                              'view-order/basic-order-info/label/will-attend/yes'
                                          )
                                        : Translate(
                                              'view-order/basic-order-info/label/will-attend/no'
                                          )}
                                </dd>
                            </Col>
                            <Col sm={6}>
                                <dt>
                                    {Translate(
                                        'view-order/basic-order-info/titles/number-of-images'
                                    )}
                                </dt>
                                <dd>{bookingInfo.numberOfImages}</dd>
                            </Col>
                        </Row>
                    ) : null}

                    <Row>
                        {Features.hasFeature(Features.UpdateImageEditorFirm, user) &&
                        bookingInfo &&
                        bookingInfo.bookableOrderType.value !== BookableOrderTypes.Styling ? (
                            <Col md={6}>
                                <dt>
                                    {Translate(
                                        'view-order/basic-order-info/titles/image-edit-firm'
                                    )}
                                </dt>
                                <dd>
                                    <FormSelect
                                        name="imageEditorFirm"
                                        value={
                                            bookingInfo.imageEditorFirmId !== undefined &&
                                            bookingInfo.imageEditorFirmId !== null
                                                ? bookingInfo.imageEditorFirmId.toString()
                                                : '-1'
                                        }
                                        onChange={updateImageEditorFirmChanged}
                                        disabled={disableImageEditorFirm(bookingInfo.photoStatus)}
                                    >
                                        <option value={'-1'}>
                                            {Translate(
                                                'view-order/basic-order-info/titles/image-edit-firm/default'
                                            )}
                                        </option>
                                        {availableImageEditorFirms.map(
                                            (x: ISelectViewModel) =>
                                                x.id === bookingInfo.imageEditorFirmId &&
                                                x.isDiscontinued === true ? (
                                                    <option
                                                        disabled={x.isDiscontinued}
                                                        key={x.id}
                                                        value={x.id.toString()}
                                                    >
                                                        {x.name}
                                                    </option>
                                                ) : x.isDiscontinued ? (
                                                    ''
                                                ) : (
                                                    <option key={x.id} value={x.id.toString()}>
                                                        {x.name}
                                                    </option>
                                                )
                                        )}
                                    </FormSelect>
                                </dd>
                            </Col>
                        ) : null}

                        {Features.hasFeature(Features.OrderUpdateImageEditor, user) &&
                        bookingInfo &&
                        bookingInfo.bookableOrderType.value !== BookableOrderTypes.Styling ? (
                            <Col md={6}>
                                <dt>
                                    {Translate('view-order/basic-order-info/titles/image-editor')}
                                </dt>
                                <dd>
                                    <FormSelect
                                        name="imageEditor"
                                        value={
                                            bookingInfo.imageEditorId !== undefined &&
                                            bookingInfo.imageEditorId !== null
                                                ? bookingInfo.imageEditorId.toString()
                                                : '-1'
                                        }
                                        onChange={updateImageEditorChanged}
                                        disabled={disableImageEditor(bookingInfo)}
                                    >
                                        <option value={'-1'}>
                                            {Translate(
                                                'view-order/basic-order-info/titles/image-editor/default'
                                            )}
                                        </option>
                                        {availableImageEditors
                                            .filter(
                                                (x) =>
                                                    x.imageEditorFirmId &&
                                                    bookingInfo.imageEditorFirmId &&
                                                    x.imageEditorFirmId.toString() ===
                                                        bookingInfo.imageEditorFirmId.toString()
                                            )
                                            .map((x: IImageEditorViewModel) => (
                                                <option key={x.id} value={x.id.toString()}>
                                                    {x.name}
                                                </option>
                                            ))}
                                    </FormSelect>
                                </dd>
                            </Col>
                        ) : null}
                    </Row>
                    {bookingInfo.preferredTimeOfPhotographing ? (
                        <div>
                            <dt>
                                {Translate('view-order/basic-order-info/titles/requested-time')}
                            </dt>
                            <dd style={{ display: 'flex' }}>
                                {bookingInfo.preferredTimeOfPhotographing ? (
                                    <DateTimeView
                                        dateTime={bookingInfo.preferredTimeOfPhotographing}
                                        format="YYYY-MM-DD"
                                    />
                                ) : (
                                    ''
                                )}
                                {bookingInfo.preferredTimeOfPhotographingSlot ? (
                                    <p style={{ marginLeft: 5 }}>
                                        {bookingInfo.preferredTimeOfPhotographingSlot}
                                    </p>
                                ) : (
                                    ''
                                )}
                            </dd>
                        </div>
                    ) : null}
                    <Row>
                        {bookingInfo.bookedTime ? (
                            <Col sm={6}>
                                <dt>
                                    {Translate('view-order/basic-order-info/titles/booked-time')}
                                </dt>
                                <dd>
                                    <DateTimeView dateTime={bookingInfo.bookedTime} />
                                </dd>
                            </Col>
                        ) : null}
                        {bookingInfo.bookedTime ? (
                            <Col sm={6}>
                                <dt>
                                    {Translate(
                                        'view-order/basic-order-info/titles/calculated-end-time'
                                    )}
                                </dt>
                                <dd>
                                    <DateTimeView dateTime={bookingInfo.calculatedEndTime} />
                                </dd>
                            </Col>
                        ) : null}
                    </Row>

                    {bookingInfo.keyPickupAddress ? (
                        <div>
                            <dt>{Translate('product-property/key-pickup')}</dt>
                            <dd>
                                <i style={{ marginRight: '6px' }} className="material-icons">
                                    vpn_key
                                </i>
                                {bookingInfo.keyPickupAddress}
                                {bookingInfo.keyPickupZip || bookingInfo.keyPickupCity
                                    ? `, ${[bookingInfo.keyPickupZip, bookingInfo.keyPickupCity]
                                          .filter(Boolean)
                                          .join(' ')}`
                                    : null}
                            </dd>
                        </div>
                    ) : null}

                    {bookingInfo.notes ? (
                        <div>
                            <dt>{Translate('view-order/basic-order-info/titles/notes')}</dt>
                            <dd>{bookingInfo.notes}</dd>
                        </div>
                    ) : null}

                    {canWriteMessageToPhotographer ? (
                        <div>
                            <dt>{Translate('view-order/basic-order-info/modal/message-agent-to-photographer/title')}</dt>
                            <dd>
                                <Modal show={isMsgAgentToPhotographerModalVisible} onHide={() => setIsMsgAgentToPhotographerModalVisible(false)}>
                                    <Modal.Header closeButton>
                                        <Modal.Title>{Translate('view-order/basic-order-info/modal/message-agent-to-photographer/title')}</Modal.Title>
                                    </Modal.Header>
                                    <Modal.Body>
                                        <FormGroup>
                                            <AutoResizeTextArea
                                                name="basicOrderInfo.msgAgentToPhotographer"
                                                value={msgAgentToPhotographer}
                                                maxHeightPx={300}
                                                onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
                                                    setMsgAgentToPhotographer(event.target.value);
                                                }}
                                            />
                                        </FormGroup>
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <Button onClick={() => setIsMsgAgentToPhotographerModalVisible(false)}>
                                            {Translate('view-order/basic-order-info/modal/message-agent-to-photographer/button/cancel')}
                                        </Button>
                                        <Button
                                            bsStyle="primary"
                                            onClick={() => {
                                                bookingInfo.commentsToPhotographerFromAgent = msgAgentToPhotographer;
                                                saveComment(msgAgentToPhotographer, CommentType.FROM_AGENT_TO_PHOTOGRAPHER);
                                                setIsMsgAgentToPhotographerModalVisible(false);
                                            }}
                                        >
                                            {Translate('view-order/basic-order-info/modal/message-agent-to-photographer/button/save')}
                                        </Button>
                                    </Modal.Footer>
                                </Modal>
                                {bookingInfo.commentsToPhotographerFromAgent ? (
                                    <div style={{ marginTop: '5px' }}>{bookingInfo.commentsToPhotographerFromAgent}</div>
                                ) : null}
                                <Button style={{ marginTop: '5px' }} onClick={() => {
                                    setMsgAgentToPhotographer(bookingInfo.commentsToPhotographerFromAgent);
                                    setIsMsgAgentToPhotographerModalVisible(true);
                                }}>
                                    {Translate('view-order/basic-order-info/modal/message-agent-to-photographer/button/edit-comment')}
                                </Button>                            
                            </dd>
                        </div>
                    ) : null}

                    {bookingInfo.delivered ? (
                        <div>
                            <dt>{Translate('view-order/basic-order-info/titles/delivered')}</dt>
                            <dd>
                                <DateTimeView dateTime={bookingInfo.delivered} />
                            </dd>
                        </div>
                    ) : null}

                    {Features.hasFeature(Features.UpdateIsDirectDelivery, user) ? (
                        <div>
                            <Checkbox
                                disabled={!canChangeIsDirectDelivery(bookingInfo.photoStatus.value)}
                                name="isDirectDelivery"
                                checked={
                                    bookingInfo.isDirectDelivery
                                        ? bookingInfo.isDirectDelivery
                                        : false
                                }
                                onChange={onIsDirectDeliveryChanged}
                            >
                                <span>
                                    {Translate(
                                        'view-order/basic-order-info/titles/is-direct-delivery'
                                    )}
                                </span>
                            </Checkbox>
                        </div>
                    ) : null}

                    {!bookingInfo.isArchived &&
                    Features.hasFeature(Features.SetOrderToUploader, user) &&
                    numberOfOrderRows > 0 &&
                    bookingInfo.photoStatus.value !== PhotoStatus.Uploading ? (
                        <ButtonGroup>
                            <Button className="btn btn-primary" onClick={setOrderToUploader}>
                                {Translate(
                                    'view-order/basic-order-info/set-order-to-uploader/button'
                                )}
                            </Button>
                        </ButtonGroup>
                    ) : null}

                    {!bookingInfo.isArchived &&
                    Features.hasFeature(Features.MarkBookingOrderAsUnread, user) ? (
                        <Button className="btn btn-primary" onClick={markAsUnread}>
                            {Translate('view-order/basic-order-info/button/mark-as-unread')}
                        </Button>
                    ) : null}

                    {bookingInfo.archiveStatus &&
                    isPhotoOrderArchived(bookingInfo.archiveStatus.value) &&
                    !bookingInfo.unarchiveStatus &&
                    Features.hasFeature(Features.ViewArchivedOrder, user) ? (
                        <dt>
                            {Translate('view-order/basic-order-info/titles/archived')}{' '}
                            {Features.hasFeature(
                                Features.ViewArchiveStatusForArchivedOrder,
                                user
                            ) && <span>({Translate(bookingInfo.archiveStatus.displayName)})</span>}
                        </dt>
                    ) : null}

                    {bookingInfo.archiveStatus &&
                    isPhotoOrderArchived(bookingInfo.archiveStatus.value) &&
                    !bookingInfo.unarchiveStatus &&
                    Features.hasFeature(Features.ViewArchivedOrder, user) ? (
                        <dd>
                            <DateTimeView dateTime={bookingInfo.dateArchived} />
                            <br />

                            {bookingInfo.canRestoreArchivedOrder &&
                            !bookingInfo.unarchiveStatus &&
                            Features.hasFeature(Features.UnarchiveArchivedOrder, user) ? (
                                <Button onClick={unarchivePhoto}>
                                    {Translate('view-order/basic-order-info/button/unarchive')}
                                </Button>
                            ) : null}

                            {Features.hasFeature(
                                Features.ViewSampleImagesForArchivedOrder,
                                user
                            ) ? (
                                <Button
                                    bsStyle="primary"
                                    bsSize="sm"
                                    onClick={() => onToggleArchiveSampleImagesModal(true)}
                                >
                                    <i className="material-icons md-18">image</i>{' '}
                                    {Translate(
                                        'view-order/basic-order-info/simaple-images-modal/view-button'
                                    )}
                                </Button>
                            ) : null}

                            {Features.hasFeature(Features.ViewSampleImagesForArchivedOrder, user) &&
                            showArchiveSampleImagesModal ? (
                                <ArchiveSampleImagesModal
                                    close={() => onToggleArchiveSampleImagesModal(false)}
                                    images={bookingInfo.archiveSampleImages}
                                />
                            ) : null}
                        </dd>
                    ) : null}

                    {bookingInfo.unarchiveStatus ? (
                        <dt>
                            {Translate('view-order/basic-order-info/titles/unarchive')}{' '}
                            {Features.hasFeature(Features.ViewUnarchiveStatusForOrder, user) &&
                                bookingInfo.unarchiveStatus.value !== UnarchiveStatus.restored && (
                                    <span>
                                        ({Translate(bookingInfo.unarchiveStatus.displayName)})
                                    </span>
                                )}
                        </dt>
                    ) : null}

                    {bookingInfo.unarchiveStatus ? (
                        <dd>
                            {(bookingInfo.unarchiveStatus.value ===
                                UnarchiveStatus.glacierRetrieval ||
                                bookingInfo.unarchiveStatus.value ===
                                    UnarchiveStatus.restoringFromCold ||
                                bookingInfo.unarchiveStatus.value ===
                                    UnarchiveStatus.restoringFromGlacier) && (
                                <div>
                                    {Translate('view-order/basic-order-info/unarchive-in-progress')}
                                </div>
                            )}

                            {bookingInfo.unarchiveStatus.value === UnarchiveStatus.restored && (
                                <div>
                                    {Translate('view-order/basic-order-info/info/rearchive', {
                                        rearchiveDate: bookingInfo.rearchiveDate
                                            ? moment(
                                                  bookingInfo.rearchiveDate.localDateTime
                                              ).format('L LT')
                                            : ''
                                    })}
                                </div>
                            )}
                        </dd>
                    ) : null}
                </dl>
                <div>
                    {photoBookingDecision.canCancel ? (
                        <Button
                            className="btn btn-danger"
                            data-testid="unbook-photographer"
                            onClick={unbookPhotographer}
                        >
                            {Translate('view-order/basic-order-info/button/unbook')}
                        </Button>
                    ) : null}

                    {photoBookingDecision.canBook ? (
                        <>
                            {bookingInfo.bookedTime ? (
                                <Button
                                    className="btn btn-primary"
                                    data-testid="rebook-photographer"
                                    onClick={goToBooking}
                                    disabled={photoBookingDecision.isBookingLocked}
                                    title={lockPhotographerReason(bookingInfo)}
                                >
                                    {Translate('view-order/basic-order-info/button/rebook')}
                                </Button>
                            ) : (
                                <Button className="btn btn-primary" onClick={goToBooking}>
                                    {Translate('view-order/basic-order-info/button/book')}
                                </Button>
                            )}
                            {Features.hasFeature(Features.LockPhotographer, user) &&
                            bookingInfo.bookedTime &&
                            lockPhotographer(bookingInfo) ? (
                                <Button className="btn" onClick={unlockPhotographer}>
                                    <i className="material-icons md-18">lock_open</i>{' '}
                                    {Translate(
                                        'view-order/basic-order-info/button/unlock-photographer'
                                    )}
                                </Button>
                            ) : null}
                            {Features.hasFeature(Features.LockPhotographer, user) &&
                            bookingInfo.bookedTime &&
                            !lockPhotographer(bookingInfo) ? (
                                <Button className="btn" onClick={lockPhotographerAction}>
                                    <i className="material-icons md-18">lock</i>{' '}
                                    {Translate(
                                        'view-order/basic-order-info/button/lock-photographer'
                                    )}
                                </Button>
                            ) : null}
                        </>
                    ) : null}
                </div>
            </Panel.Body>
        </Panel>
    );
}

export default PhotoOrderInfo;
